/**
 * Classify a plugin tool-call event as a memory write.
 *
 * Pattern matching lives in the Rust core (`memory_classifier` module);
 * this file is a thin NAPI wrapper. Same public API as before —
 * `event` and `ctx` are typed `unknown` so plugins pass their native
 * hook payloads unmodified.
 *
 * Browser callers get the compiled shim in `src-ts/browser/`.
 */
import { native } from "../native.js";
import type { MemoryEntry } from "../types.js";

/**
 * Tool names that indicate a memory write. Lowercase — matched
 * case-insensitively so OpenClaw (lowercase) and Claude API family
 * (TitleCase) both hit.
 */
export const DEFAULT_MEMORY_WRITE_TOOL_NAMES: ReadonlyArray<string> =
  native.defaultMemoryWriteToolNames();

/**
 * File path substrings that indicate a memory-shaped target. Callers
 * extend or override via `classifyMemoryWrite` options.
 */
export const DEFAULT_MEMORY_PATH_PATTERNS: ReadonlyArray<string> =
  native.defaultMemoryPathPatterns();

/**
 * Minimal shape of a `before_tool_call` context object across plugins.
 * The classifier reads only these fields — anything else is ignored.
 */
export interface ClassifierToolContext {
  tool?: { name?: string };
  toolName?: string;
  name?: string;
  params?: unknown;
  args?: unknown;
  arguments?: unknown;
}

/**
 * Minimal shape of a `before_tool_call` event object across plugins.
 */
export interface ClassifierToolEvent {
  toolName?: string;
  params?: unknown;
  args?: unknown;
  arguments?: unknown;
}

export interface ClassifyMemoryWriteOptions {
  /** Override default memory-path patterns. */
  patterns?: ReadonlyArray<string>;
  /** Override default memory-write tool names. */
  toolNames?: ReadonlyArray<string>;
}

/**
 * If this tool call is writing to a memory-shaped path, return a
 * `MemoryEntry` suitable for `scanMemory` / `guardMemoryWrite`.
 * Otherwise return `null` — the SDK's memory path is skipped and the
 * caller can fall through to a regular tool-call audit.
 *
 * Empty-content writes return `null`: they can't carry a poisoning
 * payload, and the regular audit path still sees them.
 */
export function classifyMemoryWrite(
  event: unknown,
  ctx: unknown,
  opts: ClassifyMemoryWriteOptions = {},
): MemoryEntry | null {
  return native.classifyMemoryWrite(event, ctx, {
    patterns: opts.patterns ? [...opts.patterns] : undefined,
    toolNames: opts.toolNames ? [...opts.toolNames] : undefined,
  });
}
